import useSWR, { SWRResponse } from 'swr';
import useSWRMutation from 'swr/mutation';
import {
    TimelineObjDto,
    CreateSubjectTagModel,
    SubjectDetailsModel,
    SubjectMonitoringTagGridDto,
    SubjectsModel,
    SiteModel,
    SmPanelConfigDto,
    DataListingDropdownModel,
    ConfiguredDataListingsDropdownModel,
    ConfiguredPanelsDropdownModel,
    StudySubjectDto,
} from '@interfaces-clean/subject-monitoring.interface';
// @ts-expect-error @types collides with node_modules/@types
import { components } from '@types/rest-schema';
import { getData, postData, ValidationErrorResponse } from '@apis/rest-client';

const subjectMonitoringUrl = 'SubjectMonitoring';
const timeout = 30000;

export const SubjectMonitoringAPI = {
    /**
     * Retrieves subjects for a given dataset.
     * Retrieves the latest subject number from latest dataset, not the subject number for that specific dataset
     * @param studyRef The study ref
     * @param datasetId The dataset ID
     */
    useGetSubjects: (studyRef: string, datasetId: number) => {
        return useSWR<SubjectsModel[]>(
            datasetId === 0 ? null : `${subjectMonitoringUrl}/Subjects/${studyRef}/${datasetId}`,
            (url: string) => getData<SubjectsModel[]>(url, { timeout }),
            { fallbackData: [] },
        );
    },

    /**
     * Retrieves all the `StudySubjectDto` records from `study_subjects` for the given study.
     * @param studyRef The study ref
     */
    useGetStudySubjects: (studyRef: string) => {
        return useSWR<StudySubjectDto[]>(
            `${subjectMonitoringUrl}/StudySubjects/${studyRef}`,
            (url: string) => getData<StudySubjectDto[]>(url, { timeout }),
            { fallbackData: [] },
        );
    },

    useGetSubjectDetails: (studyRef: string, datasetId: number, subjectId: number) => {
        return useSWR(`${subjectMonitoringUrl}/Subjects/${studyRef}/${datasetId}/${subjectId}`, (url) => {
            return getData<SubjectDetailsModel>(url, { timeout });
        }) as SWRResponse<SubjectDetailsModel>;
    },

    /**
     * Conditional query to get site by `siteId`.
     *
     * If `siteId` is defined, query is made.
     *
     * If `siteId` is null, no query is made.
     */
    useConditionalGetSiteById: (studyRef: string, datasetId: number, siteId: number | null) => {
        // Use null as key if subjectDetails aren't defined. This causes the query to not run.
        const key = siteId != null ? `${subjectMonitoringUrl}/Sites/${studyRef}/${datasetId}/${siteId}` : null;

        return useSWR(key, (url) => {
            // This will only be reached if the key was defined
            return getData<SiteModel>(url, { timeout });
        }) as SWRResponse<SiteModel>;
    },

    /**
     * Gets panel data for study/dataset/subject, for the specified `panelIds`
     *
     * Does not run if `panelIds` is null/undefined or empty.
     */
    useGetTimelinePanelsDataForSubject: (
        studyRef: string,
        datasetId: number,
        subjectId: number,
        panelIds?: string[] | null | undefined,
    ) => {
        type GetPanelsDataForSubjectModel = components['schemas']['GetPanelsDataForSubjectModel'];

        const url = `${subjectMonitoringUrl}/TimelineData/${studyRef}/${datasetId}/${subjectId}`;
        const resolvedPanelIds = panelIds ?? [];
        const idsKey = resolvedPanelIds
            .toSorted((a, b) => a.localeCompare(b)) // Sort so that same ids in different order is not considered "different"
            .join(',');

        // Only perform the query if there are panelIds to use
        const key = resolvedPanelIds.length > 0 ? [url, idsKey] : null;

        return useSWR<TimelineObjDto, ValidationErrorResponse>(key, () => {
            const arg: GetPanelsDataForSubjectModel = {
                panelIds: resolvedPanelIds,
            };

            return postData<GetPanelsDataForSubjectModel, TimelineObjDto>(url, { arg });
        });
    },

    useCreateBatchTagInstances: (studyRef: string) => {
        return useSWRMutation(
            `${subjectMonitoringUrl}/Tags/Add/${studyRef}`,
            (url: string, { arg }: { arg: CreateSubjectTagModel }) => {
                return postData<CreateSubjectTagModel, boolean>(url, { arg, timeout });
            },
        );
    },

    useUpdateSubjectTagInstances: (studyRef: string) => {
        return useSWRMutation(
            `${subjectMonitoringUrl}/Tags/Update/${studyRef}`,
            (url: string, { arg }: { arg: CreateSubjectTagModel }) => {
                return postData<CreateSubjectTagModel, boolean>(url, { arg, timeout });
            },
        );
    },

    useGetSelectedTagsForSubject: (studyRef: string, subjectCode: string | null) => {
        return useSWRMutation(`${subjectMonitoringUrl}/AllTags/${studyRef}/${subjectCode}`, (url: string) => {
            return getData<number[]>(url, { timeout });
        });
    },

    useGetSelectedTagsForSubjectQuery: (studyRef: string, subjectCode: string | null) => {
        return useSWR(`${subjectMonitoringUrl}/AllTags/${studyRef}/${subjectCode}`, (url: string) => {
            return getData<number[]>(url, { timeout });
        });
    },

    useGetSubjectTagsGrid: (studyRef: string, subjectCode: string, showHistory: boolean) => {
        return useSWRMutation(
            `${subjectMonitoringUrl}/Tags/SubjectTags/${studyRef}/${subjectCode}?showHistory=${showHistory}`,
            (url: string) => {
                return getData<SubjectMonitoringTagGridDto[]>(url, { timeout });
            },
        );
    },

    useGetSmPanelNames: (studyRef: string, arg: ConfiguredPanelsDropdownModel) => {
        const urlToUse = `${subjectMonitoringUrl}/PanelNames/${studyRef}`;

        const ids =
            arg.configuredPanelIds // Get sorted ids joined into key
                ?.toSorted((a, z) => a.toLowerCase().localeCompare(z.toLowerCase()))
                .join(',') ?? '';

        // If no ids given, don't send request
        const key = ids.length > 0 ? [urlToUse, ids] : null;

        // Since ids is part of the key, if it changes, request is resent
        return useSWR(key, ([url]) => {
            return postData<ConfiguredPanelsDropdownModel, SmPanelConfigDto[]>(url, { arg, timeout });
        });
    },

    useGetSmDataListings: (studyRef: string, arg: ConfiguredDataListingsDropdownModel) => {
        const urlToUse = `${subjectMonitoringUrl}/SmDataListingsDropdown/${studyRef}`;

        const ids =
            arg.configuredDataTableIds?.toSorted((a, z) => a.toLowerCase().localeCompare(z.toLowerCase())).join(',') ??
            '';

        const key = ids.length > 0 ? [urlToUse, ids] : null;

        return useSWR(key, ([url]) => {
            return postData<ConfiguredDataListingsDropdownModel, DataListingDropdownModel[]>(url, { arg, timeout });
        });
    },
} as const;
